import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { MailPlus, Plus } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../../lib/auth";
import { cn } from "../../lib/utils";
import { InitialsAvatar } from "../../components/initials-avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  accessApi,
  useAccessStatus,
  useAccountSessions,
  useAdminAccounts,
  useInvalidateAccounts,
  type AccountPatch,
  type AccountView,
  type LoginSession,
  type RoleDef,
} from "../access/api";
import { CODE_LENGTH, isCodeComplete, looksLikeEmail, normalizeCode } from "../access/codes";
import { isElevationCancelled, useElevation } from "../access/elevation";
import { readFailure } from "../access/failure";
import { SessionsTable } from "../access/sessions-table";
import {
  STATE_LABEL_KEY,
  accountActions,
  changeOutcome,
  rolesChoiceValid,
  toggleRole,
  visibleAccounts,
} from "./accounts";
import { ROLE_HINT_KEY, roleLabel, sortedRoles } from "./roles";

/**
 * The account administration (M-006, [F-028]) on the accounts-rbac module:
 * create and invite, the invitation again, roles (several, never none -
 * [F-026]), retire and reactivate (never delete), an account's sessions
 * ([NF-008]), and - for the system account - the recovery of an address
 * nobody can reach any more ([F-035]).
 *
 * Creating and changing accounts needs an elevated session ([F-033]): every
 * such request goes through `withElevation`, so the step-up dialog appears
 * when - and only when - the server asks for it. A change that would leave no
 * active administrator asks for the code sent to the system account
 * ([F-032]); every other refusal is the server's sentence, shown as it is.
 */
export function AdminUsersPage(): JSX.Element {
  const { t } = useTranslation();
  const status = useAccessStatus();

  if (status.isPending) {
    return (
      <p data-testid="admin-users_loading" className="text-sm text-muted-foreground">
        {t("common.loading")}
      </p>
    );
  }
  if (status.isError) {
    return (
      <p data-testid="admin-users_forbidden" role="alert" className="text-sm text-muted-foreground">
        {t("adminUsers.forbidden")}
      </p>
    );
  }
  return <Accounts roles={status.data.authorization.roles} />;
}

/** A pending change that waits for the system account's code ([F-032]). */
interface LastAdminChange {
  account: AccountView;
  patch: AccountPatch;
  message: string;
  done: string;
}

type Confirm = { kind: "retire" | "reactivate"; account: AccountView };

type ChangeResult = "done" | "cancelled" | "answered" | "failed";

function Accounts({ roles }: { roles: RoleDef[] }): JSX.Element {
  const { t } = useTranslation();
  const { user, isSystem } = useAuth();
  const { withElevation } = useElevation();
  const invalidate = useInvalidateAccounts();
  const list = useAdminAccounts();

  const [query, setQuery] = useState("");
  const [showRetired, setShowRetired] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AccountView | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [sessionsOf, setSessionsOf] = useState<AccountView | null>(null);
  const [recovering, setRecovering] = useState<AccountView | null>(null);
  const [lastAdmin, setLastAdmin] = useState<LastAdminChange | null>(null);
  const [guard, setGuard] = useState<string | null>(null);

  const all = list.data ?? [];
  const shown = visibleAccounts(all, query, showRetired);
  const viewer = { selfId: user?.userId ?? null, isSystem };

  /**
   * One account change: elevated if asked, and every refusal answered.
   * Resolves "done", "cancelled" (the step-up was declined), "answered" (a
   * dialog took over - the code for the last administrator, or a refusal to
   * acknowledge) or "failed" (a toast said why).
   */
  const change = async (account: AccountView, patch: AccountPatch, done: string): Promise<ChangeResult> => {
    try {
      await withElevation(() => accessApi.updateAccount(account.id, patch));
      await invalidate();
      toast.success(done);
      return "done";
    } catch (failure) {
      if (isElevationCancelled(failure)) return "cancelled";
      const read = readFailure(failure, t("errors.saveFailed"));
      const outcome = changeOutcome(read);
      if (outcome === "last-admin-code") setLastAdmin({ account, patch, message: read.message, done });
      else if (outcome === "guard") setGuard(read.message);
      else {
        toast.error(read.message);
        return "failed";
      }
      return "answered";
    }
  };

  const resend = async (account: AccountView): Promise<void> => {
    try {
      const reply = await accessApi.resendInvite(account.id);
      // [F-034]: a send that failed is said so, not swallowed.
      if (reply.ok) toast.success(t("adminUsers.inviteSent", { email: account.email ?? "" }));
      else toast.error(t("adminUsers.inviteNotSent"));
    } catch (failure) {
      toast.error(readFailure(failure, t("errors.generic")).message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 data-testid="admin-users_heading" className="text-2xl font-semibold tracking-tight">
          {t("adminUsers.title")}
        </h1>
        {list.data ? (
          <p data-testid="admin-users_count" className="text-sm text-muted-foreground">
            {t("adminUsers.countLine", {
              shown: shown.length,
              total: all.length,
              active: all.filter((account) => account.state === "active").length,
            })}
          </p>
        ) : null}
        <p data-testid="admin-users_lead" className="max-w-3xl text-sm text-muted-foreground">
          {t("adminUsers.lead")}
        </p>
        <p data-testid="admin-users_audit-hint" className="text-sm text-muted-foreground">
          {t("adminUsers.auditHint")}{" "}
          <Link to="/admin/journal" data-testid="admin-users_journal-link" className="underline underline-offset-4">
            {t("adminUsers.toJournal")}
          </Link>
        </p>
      </div>

      <div data-testid="admin-users_toolbar" role="toolbar" aria-label={t("adminUsers.toolbar")} className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="admin-users_search"
          className="max-w-xs"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("adminUsers.searchPlaceholder")}
          aria-label={t("adminUsers.searchLabel")}
        />
        <Button
          data-testid="admin-users_show-retired"
          type="button"
          variant={showRetired ? "default" : "outline"}
          size="sm"
          aria-pressed={showRetired}
          className="min-h-11 rounded-full sm:min-h-8"
          onClick={() => setShowRetired((current) => !current)}
        >
          {t("adminUsers.showRetired")}
        </Button>
        <Button data-testid="admin-users_create" className="ml-auto min-h-11 sm:min-h-9" onClick={() => setCreating(true)}>
          <Plus aria-hidden="true" />
          {t("adminUsers.create")}
        </Button>
      </div>

      {list.isError ? (
        <div
          data-testid="admin-users_load-error"
          role="alert"
          className="flex items-center justify-between gap-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
        >
          <span>{t("errors.load")}</span>
          <Button data-testid="admin-users_load-retry" variant="outline" size="sm" onClick={() => void list.refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      ) : !list.data ? (
        <div data-testid="admin-users_list-loading" className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-16 w-full animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p data-testid="admin-users_empty" className="rounded-lg border border-dashed px-6 py-10 text-center text-sm text-muted-foreground">
          {t("adminUsers.empty")}
        </p>
      ) : (
        <ul data-testid="admin-users_list" className="divide-y rounded-lg border">
          {shown.map((account) => {
            const actions = accountActions(account, viewer);
            const name = account.display_name || t("adminUsers.noName");
            return (
              <li
                key={account.id}
                data-testid={`admin-users_row_${account.id}`}
                className={cn("flex flex-col gap-3 p-3 md:flex-row md:items-center", account.state === "retired" && "bg-muted/50 text-muted-foreground")}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <InitialsAvatar name={account.display_name || account.email || "?"} size="sm" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span data-testid={`admin-users_name_${account.id}`} className={cn("font-medium", !account.display_name && "italic text-muted-foreground")}>
                        {name}
                      </span>
                      <Badge data-testid={`admin-users_state_${account.id}`} variant="outline" className="whitespace-nowrap">
                        {t(STATE_LABEL_KEY[account.state ?? "active"])}
                      </Badge>
                    </div>
                    <div data-testid={`admin-users_email_${account.id}`} className="break-all text-xs text-muted-foreground">
                      {account.email}
                    </div>
                  </div>
                </div>
                <div data-testid={`admin-users_roles_${account.id}`} className="flex flex-1 flex-wrap items-center gap-1.5">
                  {sortedRoles(account.roles ?? []).map((role) => (
                    <Badge
                      key={role}
                      data-testid={`admin-users_role_${account.id + "." + role}`}
                      variant="secondary"
                      className="whitespace-nowrap"
                      title={ROLE_HINT_KEY[role] ? t(ROLE_HINT_KEY[role]) : undefined}
                    >
                      {roleLabel(t, role, roles.find((r) => r.id === role)?.label)}
                    </Badge>
                  ))}
                </div>
                <div
                  data-testid={`admin-users_actions_${account.id}`}
                  role="group"
                  aria-label={t("adminUsers.actionsFor", { name })}
                  className="flex flex-wrap gap-2 md:justify-end"
                >
                  {actions.editRoles ? (
                    <Button data-testid={`admin-users_edit-roles_${account.id}`} variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => setEditing(account)}>
                      {t("adminUsers.editRoles")}
                    </Button>
                  ) : null}
                  {actions.resendInvite ? (
                    <Button data-testid={`admin-users_resend_${account.id}`} variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => void resend(account)}>
                      <MailPlus aria-hidden="true" />
                      {t("adminUsers.resendInvite")}
                    </Button>
                  ) : null}
                  {actions.sessions ? (
                    <Button data-testid={`admin-users_sessions_${account.id}`} variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => setSessionsOf(account)}>
                      {t("adminUsers.sessions")}
                    </Button>
                  ) : null}
                  {actions.recover ? (
                    <Button data-testid={`admin-users_recover_${account.id}`} variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => setRecovering(account)}>
                      {t("adminUsers.recover")}
                    </Button>
                  ) : null}
                  {actions.retire ? (
                    <Button
                      data-testid={`admin-users_retire_${account.id}`}
                      variant="outline"
                      size="sm"
                      className="min-h-11 text-destructive-text hover:text-destructive-text sm:min-h-8"
                      onClick={() => setConfirm({ kind: "retire", account })}
                    >
                      {t("adminUsers.retire")}
                    </Button>
                  ) : null}
                  {actions.reactivate ? (
                    <Button data-testid={`admin-users_reactivate_${account.id}`} variant="outline" size="sm" className="min-h-11 sm:min-h-8" onClick={() => setConfirm({ kind: "reactivate", account })}>
                      {t("adminUsers.reactivate")}
                    </Button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <CreateAccountDialog key={creating ? "open" : "closed"} open={creating} roles={roles} onClose={() => setCreating(false)} />
      <RolesDialog
        key={editing?.id ?? "closed"}
        account={editing}
        roles={roles}
        onClose={() => setEditing(null)}
        onSave={async (account, next) => {
          // Closed unless a toast reported a failure the choice could fix;
          // a dialog that takes over must not open on top of this one.
          const result = await change(account, { roles: next }, t("adminUsers.rolesSaved"));
          if (result === "done" || result === "answered") setEditing(null);
        }}
      />
      <ConfirmStateDialog
        confirm={confirm}
        onClose={() => setConfirm(null)}
        onConfirm={async ({ kind, account }) => {
          setConfirm(null);
          await change(
            account,
            { state: kind === "retire" ? "retired" : "active" },
            kind === "retire" ? t("adminUsers.retired") : t("adminUsers.reactivated"),
          );
        }}
      />
      <LastAdminDialog key={lastAdmin ? `${lastAdmin.account.id}` : "closed"} pending={lastAdmin} onClose={() => setLastAdmin(null)} />
      <AccountSessionsDialog account={sessionsOf} onClose={() => setSessionsOf(null)} />
      <RecoverDialog key={recovering?.id ?? "closed"} account={recovering} onClose={() => setRecovering(null)} />
      <GuardDialog message={guard} onClose={() => setGuard(null)} />
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * The role choice ([F-026]): several at once, never none.
 * ------------------------------------------------------------------------- */

function RoleChoice({
  roles,
  value,
  onChange,
}: {
  roles: RoleDef[];
  value: string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const order = roles.map((role) => role.id);
  return (
    <fieldset data-testid="admin-users_role-choice" role="group" className="space-y-2">
      <legend className="mb-1 text-sm font-medium">{t("adminUsers.rolesLabel")}</legend>
      {roles.map((role) => {
        const id = `role-choice-${role.id}`;
        return (
          <label key={role.id} htmlFor={id} className="flex min-h-11 items-start gap-3 rounded-md border p-2 sm:min-h-0">
            <input
              id={id}
              type="checkbox"
              data-testid={`admin-users_role-option_${role.id}`}
              className="mt-0.5 h-4 w-4"
              checked={value.includes(role.id)}
              onChange={() => onChange(toggleRole(value, role.id, order))}
            />
            <span>
              <span className="block text-sm font-medium">{roleLabel(t, role.id, role.label)}</span>
              {ROLE_HINT_KEY[role.id] ? (
                <span className="block text-xs text-muted-foreground">{t(ROLE_HINT_KEY[role.id] ?? "")}</span>
              ) : null}
            </span>
          </label>
        );
      })}
      {!rolesChoiceValid(value) ? (
        <p data-testid="admin-users_role-choice-hint" className="text-xs text-destructive-text">
          {t("adminUsers.lastRoleHint")}
        </p>
      ) : null}
    </fieldset>
  );
}

/* ---------------------------------------------------------------------------
 * Dialogs
 * ------------------------------------------------------------------------- */

/**
 * [F-028]: the administration creates the account with its address and its
 * roles; the person sets password and display name through the invitation
 * ([F-029]). The account waits as unconfirmed until then.
 */
function CreateAccountDialog({ open, roles, onClose }: { open: boolean; roles: RoleDef[]; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { withElevation } = useElevation();
  const invalidate = useInvalidateAccounts();
  const [email, setEmail] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = looksLikeEmail(email) && rolesChoiceValid(chosen);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const reply = await withElevation(() => accessApi.createAccount({ email: email.trim(), roles: chosen }));
      await invalidate();
      if (reply.invitation_sent) toast.success(t("adminUsers.created", { email: email.trim() }));
      else toast.error(t("adminUsers.createdNotInvited"));
      onClose();
    } catch (failure) {
      if (!isElevationCancelled(failure)) setError(readFailure(failure, t("errors.saveFailed")).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent data-testid="admin-users_create-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("adminUsers.createTitle")}</DialogTitle>
          <DialogDescription>{t("adminUsers.createLead")}</DialogDescription>
        </DialogHeader>
        <form data-testid="admin-users_create-form" className="space-y-4" onSubmit={(event) => void submit(event)}>
          <div className="space-y-2">
            <Label htmlFor="admin-users-create-email">{t("adminUsers.email")}</Label>
            <Input
              id="admin-users-create-email"
              data-testid="admin-users_create-email"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <RoleChoice roles={roles} value={chosen} onChange={setChosen} />
          {error ? (
            <p data-testid="admin-users_create-error" role="alert" className="text-sm text-destructive-text">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button data-testid="admin-users_create-cancel" type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button data-testid="admin-users_create-submit" type="submit" disabled={busy || !ready}>
              {t("adminUsers.createSubmit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RolesDialog({
  account,
  roles,
  onClose,
  onSave,
}: {
  account: AccountView | null;
  roles: RoleDef[];
  onClose: () => void;
  onSave: (account: AccountView, roles: string[]) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<string[]>(() => account?.roles ?? []);
  const [busy, setBusy] = useState(false);
  const unchanged =
    chosen.length === (account?.roles ?? []).length && chosen.every((role) => (account?.roles ?? []).includes(role));

  return (
    <Dialog open={account !== null} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent data-testid="admin-users_roles-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("adminUsers.rolesTitle")}</DialogTitle>
          <DialogDescription data-testid="admin-users_roles-subject">
            {t("adminUsers.rolesLead", { name: account?.display_name || account?.email || "" })}
          </DialogDescription>
        </DialogHeader>
        <RoleChoice roles={roles} value={chosen} onChange={setChosen} />
        <DialogFooter>
          <Button data-testid="admin-users_roles-cancel" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="admin-users_roles-save"
            disabled={busy || unchanged || !rolesChoiceValid(chosen)}
            onClick={async () => {
              if (!account) return;
              setBusy(true);
              await onSave(account, chosen);
              setBusy(false);
            }}
          >
            {t("account.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Retire (destructive pattern: the consequence named, focus on "Abbrechen") or reactivate. */
function ConfirmStateDialog({
  confirm,
  onClose,
  onConfirm,
}: {
  confirm: Confirm | null;
  onClose: () => void;
  onConfirm: (confirm: Confirm) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation();
  const retire = confirm?.kind === "retire";
  const name = confirm?.account.display_name || confirm?.account.email || "";
  return (
    <Dialog open={confirm !== null} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent data-testid="admin-users_confirm-dialog">
        <DialogHeader>
          <DialogTitle data-testid="admin-users_confirm-title">
            {retire ? t("adminUsers.retireDialog.title") : t("adminUsers.reactivateDialog.title")}
          </DialogTitle>
          <DialogDescription data-testid="admin-users_confirm-question">
            {retire ? t("adminUsers.retireDialog.question", { name }) : t("adminUsers.reactivateDialog.question", { name })}
          </DialogDescription>
        </DialogHeader>
        <p data-testid="admin-users_confirm-consequence" className="text-sm">
          {retire ? t("adminUsers.retireDialog.consequence") : t("adminUsers.reactivateDialog.consequence")}
        </p>
        <DialogFooter>
          <Button data-testid="admin-users_confirm-cancel" variant="outline" autoFocus={retire} onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="admin-users_confirm-submit"
            variant={retire ? "destructive" : "default"}
            onClick={() => (confirm ? void onConfirm(confirm) : undefined)}
          >
            {retire ? t("adminUsers.retire") : t("adminUsers.reactivate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * [F-032], [AC-057]: the change would leave no active administrator. The
 * server has mailed an eight-digit code to the system account; with it the
 * same change goes through.
 */
function LastAdminDialog({ pending, onClose }: { pending: LastAdminChange | null; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { withElevation } = useElevation();
  const invalidate = useInvalidateAccounts();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!pending || !isCodeComplete(code)) return;
    setBusy(true);
    setError(null);
    try {
      await withElevation(() =>
        accessApi.updateAccount(pending.account.id, { ...pending.patch, authorization_code: normalizeCode(code) }),
      );
      await invalidate();
      toast.success(pending.done);
      onClose();
    } catch (failure) {
      if (!isElevationCancelled(failure)) setError(readFailure(failure, t("errors.saveFailed")).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={pending !== null} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent data-testid="admin-users_last-admin-dialog">
        <DialogHeader>
          <DialogTitle>{t("adminUsers.lastAdmin.title")}</DialogTitle>
          <DialogDescription data-testid="admin-users_last-admin-message">{pending?.message}</DialogDescription>
        </DialogHeader>
        <form data-testid="admin-users_last-admin-form" className="space-y-3" onSubmit={(event) => void submit(event)}>
          <p className="text-sm text-muted-foreground">{t("adminUsers.lastAdmin.lead")}</p>
          <div className="space-y-2">
            <Label htmlFor="admin-users-last-admin-code">{t("adminUsers.lastAdmin.code")}</Label>
            <Input
              id="admin-users-last-admin-code"
              data-testid="admin-users_last-admin-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={CODE_LENGTH + 2}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          {error ? (
            <p data-testid="admin-users_last-admin-error" role="alert" className="text-sm text-destructive-text">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button data-testid="admin-users_last-admin-cancel" type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button data-testid="admin-users_last-admin-submit" type="submit" disabled={busy || !isCodeComplete(code)}>
              {t("adminUsers.lastAdmin.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** [NF-008], [AC-063]: the administration ends another account's sessions. */
function AccountSessionsDialog({ account, onClose }: { account: AccountView | null; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const sessions = useAccountSessions(account?.id ?? null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const end = async (session: LoginSession): Promise<void> => {
    setBusyId(session.id);
    setError(null);
    try {
      await accessApi.revokeSession(session.id);
      await sessions.refetch();
      toast.success(t("sessions.revoked"));
    } catch (failure) {
      setError(readFailure(failure, t("errors.generic")).message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={account !== null} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent data-testid="admin-users_sessions-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("adminUsers.sessionsTitle")}</DialogTitle>
          <DialogDescription>{t("adminUsers.sessionsLead", { name: account?.display_name || account?.email || "" })}</DialogDescription>
        </DialogHeader>
        {sessions.isError ? (
          <p data-testid="admin-users_sessions-load-error" role="alert" className="text-sm text-destructive-text">
            {readFailure(sessions.error, t("errors.load")).message}
          </p>
        ) : sessions.data ? (
          <SessionsTable testid="admin-users_sessions" sessions={sessions.data} busyId={busyId} onRevoke={(session) => void end(session)} />
        ) : (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        )}
        {error ? (
          <p data-testid="admin-users_sessions-error" role="alert" className="text-sm text-destructive-text">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button data-testid="admin-users_sessions-close" variant="outline" onClick={onClose}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * [F-035], [AC-061]: the system account changes an address nobody can reach
 * any more. Two codes, both entered: one to the system account (the change
 * comes from the administration), one to the new address (it belongs to the
 * person). No session follows.
 */
function RecoverDialog({ account, onClose }: { account: AccountView | null; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const invalidate = useInvalidateAccounts();
  const [step, setStep] = useState<"request" | "confirm">("request");
  const [target, setTarget] = useState("");
  const [codeSystem, setCodeSystem] = useState("");
  const [codeNew, setCodeNew] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const request = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!account || !looksLikeEmail(target)) return;
    setBusy(true);
    setError(null);
    try {
      await accessApi.recoverRequest(account.id, target.trim());
      setStep("confirm");
    } catch (failure) {
      setError(readFailure(failure, t("errors.generic")).message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!account || !isCodeComplete(codeSystem) || !isCodeComplete(codeNew)) return;
    setBusy(true);
    setError(null);
    try {
      await accessApi.recoverConfirm(account.id, {
        new_email: target.trim(),
        code_system: normalizeCode(codeSystem),
        code_new: normalizeCode(codeNew),
      });
      await invalidate();
      toast.success(t("adminUsers.recovered", { email: target.trim() }));
      onClose();
    } catch (failure) {
      setError(readFailure(failure, t("errors.generic")).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={account !== null} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent data-testid="admin-users_recover-dialog">
        <DialogHeader>
          <DialogTitle>{t("adminUsers.recoverTitle")}</DialogTitle>
          <DialogDescription data-testid="admin-users_recover-lead">
            {t("adminUsers.recoverLead", { name: account?.display_name || "", email: account?.email ?? "" })}
          </DialogDescription>
        </DialogHeader>
        {step === "request" ? (
          <form data-testid="admin-users_recover-form" className="space-y-3" onSubmit={(event) => void request(event)}>
            <div className="space-y-2">
              <Label htmlFor="admin-users-recover-email">{t("adminUsers.recoverNewEmail")}</Label>
              <Input
                id="admin-users-recover-email"
                data-testid="admin-users_recover-email"
                type="email"
                autoComplete="off"
                value={target}
                onChange={(event) => setTarget(event.target.value)}
              />
            </div>
            <DialogFooter>
              <Button data-testid="admin-users_recover-cancel" type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button data-testid="admin-users_recover-request" type="submit" disabled={busy || !looksLikeEmail(target)}>
                {t("adminUsers.recoverRequest")}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form data-testid="admin-users_recover-confirm-form" className="space-y-3" onSubmit={(event) => void confirm(event)}>
            <p data-testid="admin-users_recover-sent" className="text-sm">
              {t("adminUsers.recoverSent", { email: target.trim() })}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="admin-users-recover-code-system">{t("adminUsers.recoverCodeSystem")}</Label>
                <Input
                  id="admin-users-recover-code-system"
                  data-testid="admin-users_recover-code-system"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={CODE_LENGTH + 2}
                  value={codeSystem}
                  onChange={(event) => setCodeSystem(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-users-recover-code-new">{t("adminUsers.recoverCodeNew")}</Label>
                <Input
                  id="admin-users-recover-code-new"
                  data-testid="admin-users_recover-code-new"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={CODE_LENGTH + 2}
                  value={codeNew}
                  onChange={(event) => setCodeNew(event.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="admin-users_recover-back" type="button" variant="outline" onClick={() => setStep("request")}>
                {t("common.back")}
              </Button>
              <Button
                data-testid="admin-users_recover-confirm"
                type="submit"
                disabled={busy || !isCodeComplete(codeSystem) || !isCodeComplete(codeNew)}
              >
                {t("adminUsers.recoverConfirm")}
              </Button>
            </DialogFooter>
          </form>
        )}
        {error ? (
          <p data-testid="admin-users_recover-error" role="alert" className="text-sm text-destructive-text">
            {error}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * A refusal by a rule of the domain - the last administrator, the Owner of
 * open incidents who would hold neither party role, a change that raced
 * another one. The server's sentence, acknowledged with one button.
 */
function GuardDialog({ message, onClose }: { message: string | null; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={message !== null} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent data-testid="admin-users_guard-dialog">
        <DialogHeader>
          <DialogTitle data-testid="admin-users_guard-title">{t("adminUsers.guardDialog.title")}</DialogTitle>
          <DialogDescription data-testid="admin-users_guard-detail">{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button data-testid="admin-users_guard-confirm" onClick={onClose}>
            {t("adminUsers.guardDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
